import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { ICONS } from "@/lib/icons";
import copy from "@/content/homepage-copy.json";

const { menu: MenuIcon, close: CloseIcon, account: AccountIcon, arrowRight: ArrowRight } = ICONS;

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
      setPastHero(window.scrollY > window.innerHeight * 0.75);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const nav = copy.navigation;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background,border] duration-300 ${
          scrolled ? "glass border-b border-brand-line" : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-content items-center justify-between px-4 md:px-8">
          <a href="#top" aria-label="Promptivo — strona główna" className="shrink-0">
            <Logo height={36} />
          </a>

          <nav aria-label="Główne" className="hidden items-center gap-9 lg:flex">
            {nav.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-${item.href.replace("#", "")}`}
                className="text-[16px] font-medium text-brand-sec transition-colors duration-200 hover:text-brand-text"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <AnimatePresence>
              {pastHero && (
                <motion.a
                  href="/rejestracja"
                  data-testid="header-cta"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 12 }}
                  transition={{ duration: 0.25 }}
                  className="hidden items-center gap-2 rounded-button bg-brand-accent px-4 py-2 text-sm font-semibold text-brand-on transition-transform duration-200 hover:-translate-y-0.5 sm:inline-flex"
                >
                  Załóż konto
                  <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
                </motion.a>
              )}
            </AnimatePresence>

            <Link
              to="/logowanie"
              data-testid="login-link"
              className="hidden text-[16px] font-medium text-brand-sec transition-colors hover:text-brand-text md:inline"
            >
              Zaloguj się
            </Link>
            <Link
              to="/konto"
              aria-label="Konto"
              data-testid="account-link"
              className="hidden h-10 w-10 items-center justify-center rounded-full border border-brand-line text-brand-sec transition-colors hover:text-brand-text md:inline-flex"
            >
              <AccountIcon size={19} strokeWidth={1.75} aria-hidden="true" />
            </Link>

            <button
              type="button"
              aria-label="Otwórz menu"
              aria-expanded={open}
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-field border border-brand-line text-brand-text lg:hidden"
            >
              <MenuIcon size={22} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] flex flex-col bg-brand-bg lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            data-testid="mobile-menu-panel"
          >
            <div className="flex h-[72px] items-center justify-between px-4">
              <Logo height={28} />
              <button
                type="button"
                aria-label="Zamknij menu"
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-field border border-brand-line text-brand-text"
              >
                <CloseIcon size={22} strokeWidth={1.75} aria-hidden="true" />
              </button>
            </div>
            <nav aria-label="Menu mobilne" className="flex flex-1 flex-col gap-1 px-4 pt-6">
              {nav.map((item, i) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.href.replace("#", "")}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.05 }}
                  className="border-b border-brand-line py-4 font-display text-2xl font-medium text-brand-text"
                >
                  {item.label}
                </motion.a>
              ))}
            </nav>
            <div className="px-4 pb-10">
              <a
                href="/rejestracja"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-cta"
                className="flex items-center justify-center gap-2 rounded-button bg-brand-accent px-6 py-4 text-base font-semibold text-brand-on"
              >
                Załóż bezpłatne konto
                <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
